/**
 * ArtefactsList Component
 *
 * Displays artefacts in a single column with inline viewer.
 * Shows artefacts in an accordion layout within tabs.
 */

import React, { useRef, useEffect } from "react";
import { Chip } from "@heroui/react";
import { Icon } from "@iconify/react/dist/iconify.js";

import { useArtefactStore } from "@/stores/ArtefactStore";
import { Artefact, ARTEFACT_TYPE_META, ArtefactType } from "@/types/artefacts";
import { parseTableContent, getTableDimensions, getTablePreview } from "@/utils/tableUtils";
import {
  generateDocumentMetadata,
  generateDocumentPreview,
  formatWordCount,
  formatReadingTime,
} from "@/utils/documentUtils";
import { useResizableArtefactCard } from "@/hooks/useResizableArtefactCard";
import { MIN_ARTEFACT_HEIGHT } from "@/utils/artefactHeight";

import { ArtefactViewer } from "./ArtefactViewer";
import { ArtefactResizeHandle } from "./ArtefactResizeHandle";

/**
 * Strip markdown formatting from text
 */
const stripMarkdown = (text: string): string => {
  return text
    .replace(/#{1,6}\s+/g, "") // Remove heading markers
    .replace(/\*\*(.*?)\*\*/g, "$1") // Remove bold
    .replace(/\*(.*?)\*/g, "$1") // Remove italic
    .replace(/`(.*?)`/g, "$1") // Remove inline code
    .replace(/\[(.*?)\]\(.*?\)/g, "$1") // Remove links, keep text
    .replace(/!\[(.*?)\]\(.*?\)/g, "$1") // Remove images, keep alt text
    .replace(/>/g, "") // Remove blockquote
    .replace(/[-*+]\s+/g, "") // Remove list markers
    .replace(/\d+\.\s+/g, "") // Remove numbered list markers
    .trim();
};

export const ArtefactsList: React.FC = () => {
  const { getFilteredArtefactsForCurrentChat, selectedArtefactIds, selectMultipleArtefacts } =
    useArtefactStore();

  const artefacts = getFilteredArtefactsForCurrentChat();
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    artefactHeight,
    isResizingArtefact,
    handleArtefactResizeMouseDown,
    handleArtefactResizeKeyboard,
    resetArtefactHeight,
    maxArtefactHeight,
  } = useResizableArtefactCard();

  // Scroll to selected artefacts when selection changes externally
  useEffect(() => {
    if (selectedArtefactIds.length === 0 || !containerRef.current) return;

    const timer = setTimeout(() => {
      const firstSelectedId = selectedArtefactIds[0];
      const artefactItem = containerRef.current?.querySelector(
        `[data-artefact-id="${firstSelectedId}"]`,
      );

      if (artefactItem) {
        artefactItem.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }, 150);

    return () => clearTimeout(timer);
  }, [selectedArtefactIds]);

  // Toggle an artefact's expanded state
  const handleToggle = (artefactId: string) => {
    const isSelected = selectedArtefactIds.includes(artefactId);

    if (isSelected) {
      selectMultipleArtefacts(selectedArtefactIds.filter((id) => id !== artefactId));
    } else {
      selectMultipleArtefacts([...selectedArtefactIds, artefactId]);
    }
  };

  const formatRelativeTime = (timestamp: string): string => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffMs = now.getTime() - time.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;

    const diffHours = Math.floor(diffMins / 60);

    if (diffHours < 24) return `${diffHours}h ago`;

    const diffDays = Math.floor(diffHours / 24);

    return `${diffDays}d ago`;
  };

  const getArtefactPreview = (artefact: Artefact): string => {
    switch (artefact.type) {
      case ArtefactType.TABLE: {
        const tableData = parseTableContent(artefact.content);

        if (tableData) {
          return getTablePreview(tableData.headers, tableData.rows);
        }
        break;
      }
      case ArtefactType.DOCUMENT: {
        return generateDocumentPreview(artefact.content);
      }
      case ArtefactType.HTML: {
        // Show user-friendly preview for HTML artefacts instead of raw HTML code
        const isChart =
          artefact.title.toLowerCase().includes("chart") ||
          artefact.content.includes("canvas") ||
          artefact.content.includes("Chart");

        return isChart ? "📊 Interactive Chart" : "🌐 HTML Content";
      }
      case ArtefactType.GRAPH: {
        const nodeCount = artefact.metadata?.nodeCount ?? "?";
        const edgeCount = artefact.metadata?.edgeCount ?? "?";

        return `Graph visualisation with ${nodeCount} nodes and ${edgeCount} edges`;
      }
      default:
        break;
    }

    // Fallback for other types
    const maxLength = 100;
    const content = artefact.content.trim();

    if (content.length <= maxLength) return content;

    return content.substring(0, maxLength) + "...";
  };

  const getArtefactMetadata = (artefact: Artefact): string => {
    const parts: string[] = [];

    switch (artefact.type) {
      case ArtefactType.TABLE: {
        const tableData = parseTableContent(artefact.content);

        if (tableData) {
          parts.push(getTableDimensions(tableData));
        }
        break;
      }
      case ArtefactType.DOCUMENT: {
        const metadata = generateDocumentMetadata(artefact.content);

        parts.push(formatWordCount(metadata.wordCount));
        parts.push(formatReadingTime(metadata.readingTime));
        break;
      }
      default:
        break;
    }

    // Add estimated size if available
    if (artefact.metadata?.estimatedSize) {
      parts.push(artefact.metadata.estimatedSize);
    }

    // Add timestamp
    parts.push(formatRelativeTime(artefact.timestamp));

    return parts.join(" • ");
  };

  if (artefacts.length === 0) {
    return (
      <div className="h-full flex items-center justify-center p-6">
        <div className="text-center text-default-500">
          <div className="text-4xl mb-3">🎨</div>
          <p className="text-sm">No artefacts detected yet</p>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="h-full overflow-y-auto px-4 py-3">
      <div className="flex flex-col gap-2">
        {artefacts.map((artefact) => {
          const typeMeta = ARTEFACT_TYPE_META[artefact.type];
          const isExpanded = selectedArtefactIds.includes(artefact.id);

          return (
            <div
              key={artefact.id}
              className="rounded-lg border border-default-200 overflow-hidden"
              data-artefact-id={artefact.id}
            >
              <button
                aria-expanded={isExpanded}
                aria-label={artefact.title}
                className="w-full text-left px-3 py-2.5 hover:bg-default-50 transition-colors"
                onClick={() => handleToggle(artefact.id)}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg bg-default-100 ${typeMeta.color}`}>
                    <Icon height={16} icon={typeMeta.icon} width={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium text-default-800">
                        {stripMarkdown(artefact.title)}
                      </span>
                      <Chip className="text-xs" size="sm" variant="primary">
                        {typeMeta.label}
                      </Chip>
                    </div>
                    <div className="text-xs text-default-500">{getArtefactMetadata(artefact)}</div>
                  </div>
                  <Icon
                    className={`text-default-400 transition-transform shrink-0 ${isExpanded ? "rotate-180" : ""}`}
                    icon="solar:alt-arrow-down-linear"
                    width={16}
                  />
                </div>
                <p className="text-xs text-default-600 line-clamp-2 ml-11 mt-1">
                  {getArtefactPreview(artefact)}
                </p>
              </button>
              {isExpanded && (
                <div
                  className="bg-default-50 dark:bg-default-100 rounded-lg flex flex-col"
                  style={{ height: artefactHeight }}
                >
                  <div className="flex-1 min-h-0 overflow-hidden p-4">
                    <ArtefactViewer artefact={artefact} />
                  </div>
                  <ArtefactResizeHandle
                    isResizing={isResizingArtefact}
                    valueMax={maxArtefactHeight}
                    valueMin={MIN_ARTEFACT_HEIGHT}
                    valueNow={artefactHeight}
                    onDoubleClick={resetArtefactHeight}
                    onKeyboardResize={handleArtefactResizeKeyboard}
                    onMouseDown={handleArtefactResizeMouseDown}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
